import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Users, Plus, UserCircle, Power, Spinner, Buildings, ShieldCheck } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { tpaApi } from '../../services/api';
import api from '../../services/api';

export default function TPAPortalPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [payers, setPayers] = useState([]);
  const [form, setForm] = useState({ email: '', name: '', organization: '', payer_id: '', password: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { loadUsers(); loadPayers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try { const r = await tpaApi.users(); setUsers(r.data || []); }
    catch (e) { toast.error('Failed to load TPA users'); }
    setLoading(false);
  };

  const loadPayers = async () => {
    try { const r = await api.get('/payers/active'); setPayers(r.data || []); } catch {}
  };

  const handleCreate = async () => {
    if (!form.email || !form.name || !form.organization) {
      toast.error('Email, Name and Organization are required');
      return;
    }
    setCreating(true);
    try {
      const r = await tpaApi.createUser(form);
      toast.success(r.data?.message || 'TPA user created');
      setCreateOpen(false);
      setForm({ email: '', name: '', organization: '', payer_id: '', password: '' });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create user'); }
    setCreating(false);
  };

  const toggleUser = async (id) => {
    try {
      const r = await tpaApi.toggleUser(id);
      toast.success(r.data?.message || 'User updated');
      loadUsers();
    } catch (e) { toast.error('Failed to update user'); }
  };

  return (
    <div className="space-y-5" data-testid="tpa-portal-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold font-['Outfit'] text-slate-800 flex items-center gap-2">
            <Buildings className="h-6 w-6 text-teal-500" weight="duotone" /> TPA Portal Management
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage TPA/Insurance partner access to claims</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-teal-500 hover:bg-teal-600 text-white gap-2 text-sm" data-testid="add-tpa-user-btn">
          <Plus className="h-4 w-4" /> Add TPA User
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-200">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-slate-800">{users.length}</p>
            <p className="text-xs text-slate-500">Total TPA Users</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{users.filter(u => u.is_active).length}</p>
            <p className="text-xs text-slate-500">Active</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-slate-400">{users.filter(u => !u.is_active).length}</p>
            <p className="text-xs text-slate-500">Inactive</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="text-center py-10"><Spinner className="h-6 w-6 text-teal-500 animate-spin mx-auto" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Users className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm">No TPA users created yet</p>
          <p className="text-xs mt-1">Add TPA/Insurance users to give them portal access to claims</p>
        </div>
      ) : (
        <div className="space-y-3">
          {users.map(u => (
            <Card key={u.id} className="bg-white border border-slate-200 hover:shadow-sm transition-shadow">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${u.is_active ? 'bg-teal-100' : 'bg-slate-100'}`}>
                      <UserCircle className={`h-5 w-5 ${u.is_active ? 'text-teal-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{u.name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className="text-[9px] bg-teal-100 text-teal-700 border-0">{u.organization}</Badge>
                        {u.payer_name && <Badge className="text-[9px] bg-indigo-100 text-indigo-700 border-0">{u.payer_name}</Badge>}
                        <Badge className={`text-[9px] border-0 ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.last_login && <span className="text-[10px] text-slate-400">Last: {new Date(u.last_login).toLocaleDateString('en-IN')}</span>}
                    <Button size="sm" variant="ghost" onClick={() => toggleUser(u.id)}
                      className={u.is_active ? 'text-rose-500 hover:text-rose-600' : 'text-emerald-500 hover:text-emerald-600'}
                      data-testid={`toggle-tpa-${u.id}`}>
                      <Power className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create TPA User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2"><Plus className="h-5 w-5 text-teal-500" /> Add TPA Portal User</DialogTitle>
            <DialogDescription>Create login credentials for a TPA/Insurance partner</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs font-semibold">Email *</Label>
              <Input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="tpa.user@insurance.com" data-testid="tpa-email" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Full Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="John Doe" data-testid="tpa-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Organization *</Label>
              <Input value={form.organization} onChange={e => setForm(f => ({...f, organization: e.target.value}))} placeholder="ICICI Lombard" data-testid="tpa-org" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Linked Payer</Label>
              <Select value={form.payer_id} onValueChange={v => setForm(f => ({...f, payer_id: v}))}>
                <SelectTrigger data-testid="tpa-payer"><SelectValue placeholder="Select payer..." /></SelectTrigger>
                <SelectContent>
                  {payers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Password (auto-generated if empty)</Label>
              <Input value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} placeholder="Leave blank for auto-generate" type="password" data-testid="tpa-password" />
            </div>
            <Button onClick={handleCreate} disabled={creating} className="w-full bg-teal-500 hover:bg-teal-600 text-white gap-2" data-testid="create-tpa-submit">
              <ShieldCheck className="h-4 w-4" /> {creating ? 'Creating...' : 'Create TPA User & Send Credentials'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
